import ActiveError from './error';
import {funcRoute, exec as execActive} from './server.active';
import {pathRecursion} from './util.path-match';
import {parsePathId} from './util';
import { Config, Extends } from './index';
import {File} from './server.multipart';

import * as Role from './role';
import * as Url from './url';
import * as Router from './router';
import { getCookie, setCookie, clearCookie, CookieOpt} from './server.cookie';

export interface AppExtendedObject { 
	api?: any;
	app?: any;
	extend?: any;
	fs?: any;
	log?: any;
	config?: any;
	util?: any;
	db?: any;
}
export interface ExtendedObject extends AppExtendedObject { 
	user: object;
}
export type ExtendedAttribute = keyof ExtendedObject;
export interface MiniInfo<Ext extends ExtendedObject = {user: object}> {
	/** 是否为调试模式 */
	readonly type: string;
	/** 是否为调试模式 */
	readonly isDebug: boolean;
	/** 获取请求域名 */
	readonly domain: string;
	/** 获取请求方法 */
	readonly method: string;
	/** 获取请求路径 */
	readonly path: string;
	/** 客户端 IP */
	readonly clientIp: string[];
	getHeader(h: string): undefined | string | string[];
	setHeader(h: string, v: string | string[]): void;
	/** 自定义工具 */
	readonly util?: Ext['util'];
	/** 自定义 API */
	readonly api?: Ext['api'];
	/** 自定义 APP 接口 */
	readonly app?: Ext['app'];
	/** 自定义扩展接口 */
	readonly extend?: Ext['extend'];
	/** 自定义文件系统接口 */
	readonly fs?: Ext['fs'];
	/** 自定义日志接口 */
	readonly log?: Ext['log'];
	/** 自定义配置接口 */
	readonly config?: Ext['config'];
	/** 自定义数据库接口 */
	readonly db?: Ext['db'];
}

export interface BaseInfo<Ext extends ExtendedObject = {user: object}> extends MiniInfo<Ext> {
	/** 获取主机名 */
	readonly host: string;
	/** 获取端口 */
	readonly port: number;
	/** 获取请求来源域名 */
	readonly origin: string;
	/** 获取请求来源路径 */
	readonly referer: string;
	/** 获取请求来源域名 */
	readonly originDomain: string;
	/** 获取请求来源来源 */
	readonly originPort: number;
	/** 自定义用户相关 API */
	readonly user: Ext['user'] | null;

	/**
	 * 获取Cookie
	 * @param  {String} cname Cookie名称
	 * @return {String}       Cookie值
	 */
	getCookie(cname: string): string;
	/**
	 * 设置Cookie
	 * @param {String} cname  Cookie名称
	 * @param {String} cookie Cookie值
	 * @param {Object} opt    Cookie选项
	 */
	setCookie(cname: string, cookie: string, opt?: object): boolean;
	/**
	 * 清除Cookie
	 * @param {String} cname  Cookie名称
	 * @param {Object} opt    Cookie选项
	 */
	clearCookie(cname: string, opt?: object): void;
	/**
	 * 清除所有 Cookie
	 * @param {Object} opt    Cookie选项
	 */
	clearCookie(opt?: object): void;
	/**
	 * 获取模块的Url
	 * @param  {String} id 模块Id
	 * @return {String}    模块Id
	 */
	getUrl(id: string): string;
	/**
	 * 判断是否为授权域名
	 * @param  {Number} level 限制级别
	 */
	verifyDomain(level: number): boolean;
	/**
	 * 检查域名
	 * @param  {Number} level 限制级别
	 */
	checkDomain(level: number): boolean;
	/** 获取用户ID */
	readonly userId: string;
	/**
	 * 设置用户登录
	 * @param uid 用户ID
	 */
	login(uid: string): Promise<boolean>;
	/** 用户退出 */
	exit(): Promise<void>;
	/**
	 * 检查用户是否登录
	 * 如果用户没有登录，则报错
	 * @throws {ActiveError}	If	用户没有登录
	 */
	checkLogin(): void;
	/** 获取用户权限 */
	getPower(v?: string): Promise<any | { [key: string]: any }>;
	/**
	 * 判断用户是否有权限
	 * 特别的，如果用户有超级权限，则视为用户有任意权限
	 * @param  {...String} powers 要判断的权限
	 * @return {Boolean}          用户是否有列出的权限之一
	 */
	hasPower(...powers: (string | [string] | [string, (string | string[])])[]): Promise<boolean>;
	/**
	 * 检查用户权限
	 * 如果用户没有登录或者列出的权限用户均没有，则报错
	 * 特别的，如果用户有超级权限，则视为用户有任意权限
	 * @param  {...String} powers 检查的权限
	 * @throws {ActiveError}	If	用户没有登录或者列出的权限用户均没有
	 */
	checkPower(...powers: (string | [string] | [string, (string | string[])])[]): Promise<boolean>;
};

/** 执行模块函数 */
interface InfoExec {
	/**
	 * 
	 * @param  {String} id 模块Id
	 * @return {String}    模块Id
	 */
	(pathId: string, data: object, method: string, ...symbols: symbol[]): Promise<any>;
	(pathId: string, data: object, ...symbols: symbol[]): Promise<any>;
	(pathId: string, method?: string, ...symbols: symbol[]): Promise<any>;
	(pathId: string, ...symbols: symbol[]): Promise<any>;
}

const symbol = Object.freeze({
	restful: Symbol.for('restful'),
});
export interface Info<Ext extends ExtendedObject = {user: object}, Query extends object = {}, Body extends object = {}> extends BaseInfo<Ext> {
	/** 获取文件列表 */
	readonly files: File[];
	/** 获取查询字段 */
	readonly query: Query;
	/** 获取请求体 */
	readonly body: Body;
	/** 执行模块函数 */
	exec: InfoExec;
	symbol: typeof symbol;
};
interface InInfo<Ext extends ExtendedObject = {user: object}, Query extends object = {}, Body extends object = {}> extends Info<Ext, Query, Body> {
	init(body: any, nfiles: File[], data: any): void;
};
interface InfoInitThis<Ext extends ExtendedObject = {user: object}> {
	extends: Extends;
	recursion: pathRecursion<Ext>;
	getUrl: Url.get;
	getRole: Role.get;
	getRouter: Router.get<Ext>;
	isDebug: boolean;
	cookieConfig: object;
	tokenName: string;
	getPower(id: string): any | Promise<any>;
	util: Ext['util'];
	api: Ext['api'];
	userApi: (uid: string) => Ext['user'] | null;
	/** 用于设置登陆用户的方法 */
	login(uid: string, config: object, info: Info<Ext>): string | boolean | Promise<string | boolean>;
	/** 用于退出用户的方法 */
	exit(config: object, info: Info<Ext>): boolean | Promise<boolean>;
	/** 用于判定当前用户是否登陆的方法 */
	checkCookie(cookie: string, config: object, info: Info<Ext>): string | [string] | [string, string] | Promise<string | [string] | [string, string]>;
}
export interface Opt {
	method: string, domain: string, path: string,
	getCookie: getCookie, setCookie: setCookie,
	clearCookie: clearCookie,
	getHeader(h: string): undefined | string | string[], setHeader(h: string, v: string | string[]): void,
	clientIp: string | string[],
	isVirtual?: boolean;
}
export interface createInfo<Ext extends ExtendedObject = {user: object}> {
	(opt: Opt, query: object): Promise<InInfo<Ext>>;
}

/**
 * 封装用户信息
 * @param  {File[]} options.files         上传的文件
 * @param  {String} options.method        请求方法
 * @param  {String} options.path          请求路径
 * @param  {Function} options.getCookie   获取Cookie的基本函数
 * @param  {Function} options.setCookie   设置Cookie的基本函数
 * @param  {Function} options.clearCookie 清楚Cookie的基本函数
 * @param  {Function} options.getHeader   获取Header的基本函数
 * @param  {Function} options.setHeader   设置Header的基本函数
 * @param  {String[]} clientIp            客户端数组
 * @return {Object}                       用户信息
 */
export async function createInfo<Ext extends ExtendedObject = {user: object}>(this: InfoInitThis<Ext> | void, {
	method, path, domain,
	getCookie, setCookie, clearCookie,
	getHeader, setHeader, 
	clientIp, 
}: Opt, query: object): Promise<InInfo<Ext>> {
	let body: any, data: any;
	//用到的私有成员
	let {
		extends: extendsInt = () => ({}),
		recursion = pathRecursion,
		getUrl = Url.get,
		getRole = Role.get,
		getRouter = Router.get,
		isDebug = false,
		cookieConfig = {},
		login = () => false,
		exit = () => false,
		checkCookie = (...p: any[]) => '',
		tokenName = 'user-token',
		getPower = () => {},
		util, api,
		userApi = () => null,
	}: Partial<InfoInitThis<Ext>> = this || {};
	//文件列表
	let files: File[] = [];
	//当前的Cookie
	let cookie = getCookie(tokenName);
	//用户ID, 用户权限, 用户角色, 用户信息
	let userId: string = '', power: null | any | Promise<any> = null, role = null, info;
	// 请求端口
	let port: number = 0;
	// 请求主机名
	const host = ((getHeader('host') as string) || '').replace(/:(\d+)$/, (x: string, p: string)=>(port = Number(p), ''));
	//客户端IP
	clientIp = (Array.isArray(clientIp) ? Array.from(clientIp) : [clientIp]).filter(x => typeof x === 'string');
	// 来源
	let origin: string = (getHeader('origin') as string) || '', referer: string = (getHeader('referer') as string) || '';
	//User-Agent, Authorization
	// 来源信息
	const autoOriginInfo = /^([a-z0-9\-\+\.]+\:)\/\/([^:\/\#\?]+)((?:\:\d+)?)/i.exec(origin || referer);
	// 来源域名
	const originDomain = autoOriginInfo && autoOriginInfo[2] || '';
	// 来源端口
	const originPort = autoOriginInfo && Number(autoOriginInfo[3].substr(1) || 0) || 0;

	let userApiInfo: Ext['user'] | null = null;

	//基础配置
	let config = {};

	function exec(pathId: string, data: object, method: string, ...symbols: symbol[]): Promise<any>;
	function exec(pathId: string, data: object, ...symbols: symbol[]): Promise<any>;
	function exec(pathId: string, method?: string, ...symbols: symbol[]): Promise<any>;
	function exec(pathId: string, ...symbols: symbol[]): Promise<any>;
	async function exec(
		pathId: string,
		ndata?: object | string | symbol,
		nmethod?: string | symbol,
		...symbols: symbol[]
	): Promise<any> {
		if (typeof pathId !== 'string') { return; }
		if (!(ndata && typeof ndata === 'object')) { [ndata, nmethod] = [undefined, ndata]; }
		if (typeof ndata === 'symbol') { symbols.push(ndata); }
		if (typeof nmethod === 'symbol') { symbols.push(nmethod); }
		const restful = symbols.includes(symbol.restful);

		const newData: object = typeof ndata === 'object' && ndata || (restful ? {} : data);
		const newMethod: string = typeof nmethod === 'string' && nmethod || method;
		// 解析路径
		const [rpath, rf, appId] = parsePathId(pathId, getRole);
		for await (let [p, route, newAppId, data] of recursion(rpath, rf, appId, reqInfo, restful ? newData : undefined)) {
			for (let func of funcRoute(rpath.slice(p.length), route, newAppId, newMethod, getRouter, data)) {
				const response = await execActive(extendsInt, func, newData, reqInfo, newAppId);
				if (!response) { continue; }
				if (response.error) { throw response.error; }
				return response.value;
			}
		}
	};

	let reqInfo: InInfo<Ext> = {
		type: 'active',
		symbol,
		init(nbody: any, nfiles: File[], ndata: any) {
			body = nbody;
			files = nfiles;
			data = ndata || {};
		},
		/** @type {Boolean} 是否为调试模式 */
		get isDebug(): boolean { return isDebug; },
		/** @type {String} 获取请求方法 */
		get method(): string { return method; },
		/** @type {String} 获取请求域名 */
		get domain(): string { return domain; },
		/** @type {String} 获取请求方法 */
		get path(): string { return path; },
		/** @type {String} 获取主机名 */
		get host(): string { return host; },
		/** @type {number} 获取端口 */
		get port(): number { return port || 0; },
		/** @type {String} 获取请求来源域名 */
		get origin(): string { return origin; },
		/** @type {String} 获取请求来源路径 */
		get referer(): string { return referer; },
		/** @type {String} 获取请求来源域 */
		get originDomain(): string { return originDomain; },
		/** @type {number} 获取请求来源来源 */
		get originPort(): number { return originPort; },
		/** @type {String[]} 客户端IP */
		get clientIp(): string[] { return Array.from(clientIp); },

		get user() {
			if (!userApiInfo && userId) { userApiInfo = userApi(userId); }
			return userApiInfo;
		},

		util, api,
		getHeader, setHeader,
		/**
		 * 获取Cookie
		 * @param  {String} cname Cookie名称
		 * @return {String}       Cookie值
		 */
		getCookie(cname: string): string {
			if (cname === tokenName) { return cookie; }
			if (cname && typeof cname === 'string') { return getCookie(cname); }
			if (!cookie) { return ''; }
			return tokenName + '=' + cookie;
		},
		/**
		 * 设置Cookie
		 * @param {String} cname  Cookie名称
		 * @param {String} cookie Cookie值
		 * @param {Object} opt    Cookie选项
		 */
		setCookie(cname: string, cookie: string, opt?: CookieOpt): boolean {
			if (cname === tokenName) { return false; }
			setCookie(cname, cookie, opt || cookieConfig);
			return true;
		},
		async clearCookie(cname?: string | object, opt?: object) {
			if (typeof cname !== 'string') { [cname, opt] = ['', cname]; }
			if (cname === tokenName || !cname) { await reqInfo.exit(); }
			opt = opt || cookieConfig;
			if (cname) {
				return clearCookie(cname, opt);
			} else {
				return clearCookie(opt);
			}
		},
		/**
		 * 获取模块的Url
		 * @param  {String} id 模块Id
		 * @return {String}    模块Id
		 */
		getUrl, exec,
		/** 获取文件列表 */
		get files(): any { return files; },
		/** 获取查询字段 */
		get query(): any { return query; },
		/** 获取请求体 */
		get body(): any { return body; },
		/**
		 * 判断是否为授权域名
		 * @param  {Number} level 限制级别
		 */
		verifyDomain(level:number = 0): boolean {
			const origin = originDomain;
			if (!origin) { return false; }
			level = Math.floor(Number(level));
			if (level > 3) { level = 3; }
			switch(level) {
				default: //jsonp域名
				case 1: //AJAX域名
				case 2: //子域名
					let k = origin.indexOf('.' + host);
					if (k !== -1 && k  + 1 + host.length === origin.length) { return true; }
				case 3: //当前域名
					if (origin === host) { return true; }
			}
			return false;
		},
		/**
		 * 检查域名
		 * @param  {Number} level 限制级别
		 */
		checkDomain(level: number = 0): boolean {
			if (reqInfo.verifyDomain(level)) { return true; }
			throw new ActiveError(':domain:');
		},
		/** @type {String | null} 获取用户ID */
		get userId(): any { return userId; },
		/**
		 * 设置用户登录
		 * @param  {String} uid 用户ID
		 */
		async login(uid: string): Promise<boolean> {
			let ret: string | boolean;
			try {
				ret = await login(uid, config, reqInfo);
			} catch(e) {
				return false;
			}
			if (!(typeof ret === 'string' || typeof ret === 'boolean')) { return false; }
			if (ret === false) { return false; }
			if (ret === true) { ret = ''; }
			[userId, power, role, info, cookie] = [uid, null, null, null, ret];
			if (ret) { await setCookie(tokenName, cookie, cookieConfig); }
			return true;
		},
		/**
		 * 用户退出
		 */
		async exit(): Promise<void> {
			try { exit(config, reqInfo); } catch(e) {}
			[userApiInfo, userId, power, role, info, cookie] = [null, '', null, null, null, ''];
			await clearCookie(tokenName, cookieConfig);
		},
		/**
		 * 检查用户是否登录
		 * 如果用户没有登录，则报错
		 * @throws {ActiveError}	If	用户没有登录
		 */
		checkLogin(): void {
			if (!userId) { throw new ActiveError(':user:nologin'); }
		},
		/** 获取用户权限 */
		async getPower(v?: string): Promise<any | { [key: string]: any }> {
			if (!userId) { return v ? null : {}; }
			try {
				if (!power) { power = getPower(userId); }
				return v ? (await power || [])[v] : await power || {};
			} catch(e) {
				return v ? null : {};
			}
		},
		/**
		 * 判断用户是否有权限
		 * 特别的，如果用户有超级权限，则视为用户有任意权限
		 * @param  {...String} powers 要判断的权限
		 * @return {Boolean}          用户是否有列出的权限之一
		 */
		async hasPower(...powers: (string | [string] | [string, ...(string | string[])[]])[]): Promise<boolean> {
			if (!userId) { return false; }
			let power = await reqInfo.getPower();
			if ('::' in power) { return true; }
			if (!Object.keys(power).length) { return false; }
			powers = powers.map(p => {
				if (typeof p !== 'string') { return p; }
				let k = p.indexOf('$');
				return k > 0 ? [p.substr(0, k), p.substr(k + 1)] : [p];
			})
			for (let [p, ...ps] of powers.filter(x => Array.isArray(x) && typeof x[0] === 'string') as [string, ...(string | string[])[]][]) {
				if (!(p in power)) { continue; }
				if (!ps.length) { return true; }
				p = power[p];
				if (!Array.isArray(p)) { continue; }
				if (ps.length === 1 && Array.isArray(ps[0])) { ps = ps[0] as string[]; }
				for (let v of ps) { if (p.includes(v as string)) { return true; } }
			}
			return false;
		},
		/**
		 * 检查用户权限
		 * 如果用户没有登录或者列出的权限用户均没有，则报错
		 * 特别的，如果用户有超级权限，则视为用户有任意权限
		 * @param  {...String} powers 检查的权限
		 * @throws {ActiveError}	If	用户没有登录或者列出的权限用户均没有
		 */
		async checkPower(...powers: (string | [string] | [string, (string | string[])])[]): Promise<boolean> {
			if (!userId) { throw new ActiveError(':user:nologin'); }
			if (await reqInfo.hasPower(...powers)) { return true; }
			throw new ActiveError(':power:nopower');
		},
	};

	try {
		let uidInfo = await checkCookie(cookie, config, reqInfo);
		cookie = '';
		let uid: string = '';
		if (Array.isArray(uidInfo)) {
			[uid = '', cookie = ''] = uidInfo.filter(x => x && typeof x === 'string');
		} else {
			uid = uidInfo;
		}
		if (uid && typeof uid === 'string') { userId = uid; }
		if (cookie) { await setCookie(tokenName, cookie, cookieConfig); }
	} catch(e) {
		cookie = '';
	}
	return reqInfo;
}

export default function init<Ext extends ExtendedObject = {user: object}>(
	config: Config<Ext>,
	recursion: pathRecursion<Ext>,
	getUrl: Url.get,
	getRole: Role.get,
	getRouter: Router.get<Ext>
): createInfo<Ext> {
	return createInfo.bind({
		recursion, getUrl, getRole, getRouter, 
		get isDebug() { return config.isDebug; },
		get util() { return config.util; },
		get api() { return config.api; },
		get userApi() { return config.userApi; },
		get cookieConfig() { return config.cookie; },
		get tokenName() { return config.userManager.tokenName || 'user-token'; },
		get extends(){ return config.extends; },
		login: (uid: string, cfg: object, info: Info<Ext>) => config.userManager.login(uid, cfg, info),
		exit: (cfg: object, info: Info<Ext>) => config.userManager.exit(cfg, info),
		checkCookie: (cookie: string, cfg: object, info: Info<Ext>) => config.userManager.checkCookie(cookie, cfg, info),
		getPower: (id: string) => config.userManager.getPower(id),
	} as InfoInitThis<Ext>);
}
